import { Logo, Image } from '@/components'
import {
    ENPHASE_DEVICE_TYPES,
    GENERAC_DEVICE_TYPES,
    InverterManufacturerEnum,
    SOLAREDGE_DEVICE_TYPES, TESLA_DEVICE_TYPES,
    TESLA_INVERTER_MODELS
} from '@/enums'
import { feature } from '@/services'
import { InverterManufacturerDataType } from '@/types'

const MAINTAINER_USERNAME = 'monitoring@solarinsure.com'

const MAINTAINER_ENPHASE_ID = 1034

const MAINTAINER_SOLAREDGE_ID = 1035

const MAINTAINER_GENERAC_ID = 1036

export const INVERTER_MANUFACTURERS: InverterManufacturerDataType[] = [
    {
        key: InverterManufacturerEnum.ENPHASE,
        logo: InverterManufacturerEnum.ENPHASE,
        title: 'Enphase',
        useMicroInverters: true,
        isSupported: true,
        supportCredentials: true,
        deviceTypes: ENPHASE_DEVICE_TYPES,
        order: 1,
        maintainerUsername: MAINTAINER_USERNAME,
        maintainerId: MAINTAINER_ENPHASE_ID,
        getSiteUrl: id => `https://enlighten.enphaseenergy.com/${id ? `systems/${id}` : ''}`,
        systemIdGuide: <div>
            <h3 className="flex gap-3 items-center text-xl font-semibold">
                <Logo type={InverterManufacturerEnum.GENERAC} className="w-6"/>
                How to Find System ID
            </h3>

            <div className="p-4 mt-2 text-sm text-gray-750">
                <div className="bg-primary-25 px-5 py-4 flex flex-col gap-4">
                    <h4 className="font-semibold">
                        The short video below will guide you step-by-step on how to find the System ID
                    </h4>
                    <p>Follow these simple steps to seamlessly locate the System ID for Enphase:</p>
                    <ul className="list-disc ml-8">
                        <li>
                            <span className="font-semibold">Log into</span> the <a
                                href="https://enlighten.enphaseenergy.com"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold underline"
                            >Enphase Enlighten Portal</a>.
                        </li>
                        <li>
                            <span className="font-semibold">Select</span> the warranty holder&apos;s
                            site <span className="font-semibold">from the systems list</span>.
                        </li>
                        <li>
                            <span className="font-semibold">Click</span> on
                            the <span className="font-semibold">settings icon</span> in the top right corner.
                        </li>
                        <li>In the overview section, <span className="font-semibold">locate the System ID</span>.</li>
                    </ul>
                </div>

                <Image src="/assets/images/gifs/enphase-system-id-guide.gif"/>
            </div>
        </div>
    },
    {
        key: InverterManufacturerEnum.SOLAREDGE,
        logo: InverterManufacturerEnum.SOLAREDGE,
        title: 'SolarEdge',
        useOptimizers: true,
        isSupported: true,
        supportCredentials: true,
        deviceTypes: SOLAREDGE_DEVICE_TYPES,
        order: 2,
        maintainerUsername: MAINTAINER_USERNAME,
        maintainerId: MAINTAINER_SOLAREDGE_ID,
        getSiteUrl: id => `https://monitoring.solaredge.com/${id ? `solaredge-web/p/site/${id}` : ''}`,
        systemIdGuide: <div>
            <h3 className="flex gap-3 items-center text-xl font-semibold">
                <Logo type={InverterManufacturerEnum.SOLAREDGE} className="w-6"/>
                How to Find System ID
            </h3>

            <div className="p-4 mt-2 text-sm text-gray-750">
                <div className="bg-primary-25 px-5 py-4 flex flex-col gap-4">
                    <h4 className="font-semibold">
                        The short video below will guide you step-by-step on how to find the System ID
                    </h4>
                    <p>Follow these simple steps to seamlessly locate the System ID for SolarEdge:</p>
                    <ul className="list-disc ml-8">
                        <li>
                            <span className="font-semibold">Log into</span> the <a
                                href="https://monitoring.solaredge.com"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold underline"
                            >SolarEdge Monitoring Portal</a>.
                        </li>
                        <li>
                            <span className="font-semibold">Select</span> the warranty holder&apos;s
                            site <span className="font-semibold">from the Site Name</span>.
                        </li>
                        <li>
                            In the Site Details section, <span className="font-semibold">locate the System ID</span>.
                        </li>
                    </ul>
                </div>

                <Image src="/assets/images/gifs/solaredge-system-id-guide.gif"/>
            </div>
        </div>
    },
    {
        key: InverterManufacturerEnum.SOLAREDGE_ALTERNATIVE,
        logo: InverterManufacturerEnum.SOLAREDGE,
        title: 'SolarEdge',
        isAlternative: true,
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.AP_SYSTEMS,
        logo: InverterManufacturerEnum.AP_SYSTEMS,
        title: 'AP Systems',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.GENERAC,
        logo: InverterManufacturerEnum.GENERAC,
        title: 'Generac',
        useMicroInverters: true,
        isSupported: true,
        deviceTypes: GENERAC_DEVICE_TYPES,
        order: 3,
        maintainerUsername: MAINTAINER_USERNAME,
        maintainerId: MAINTAINER_GENERAC_ID,
        getSiteUrl: id => `https://pwrfleet.generac.com/${id ? `sites/${id}` : ''}`,
        systemIdGuide: <div>
            <h3 className="flex gap-3 items-center text-xl font-semibold">
                <Logo type={InverterManufacturerEnum.GENERAC} className="w-6"/>
                How to Find System ID
            </h3>

            <div className="p-4 mt-2 text-sm text-gray-750">
                <div className="bg-primary-25 px-5 py-4 flex flex-col gap-4">
                    <h4 className="font-semibold">
                        The short video below will guide you step-by-step on how to find the System ID
                    </h4>
                    <p>Follow these simple steps to seamlessly locate the System ID for Generac:</p>
                    <ul className="list-disc ml-8">
                        <li>
                            <span className="font-semibold">Log into</span> the <a
                                href="https://pwrfleet.generac.com"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold underline"
                            >Generac portal</a>.
                        </li>
                        <li>
                            <span className="font-semibold">Navigate</span> to
                            the <span className="font-semibold">warranty holder’s account</span> in PWRcell
                        </li>
                        <li>
                            The Site ID can be found in <span className="font-semibold">the URL of the site</span>. It
                            is the unique identifier <span className="font-semibold">after /sites/ in the URL</span>.
                        </li>
                    </ul>
                </div>

                <Image src="/assets/images/gifs/generac-system-id-guide.gif"/>
            </div>
        </div>
    },
    {
        key: InverterManufacturerEnum.GENERAC_ALTERNATIVE,
        logo: InverterManufacturerEnum.GENERAC,
        title: 'Generac',
        isAlternative: true,
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.SOL_ARK,
        logo: InverterManufacturerEnum.SOL_ARK,
        title: 'Sol-Ark',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.HOYMILES,
        logo: InverterManufacturerEnum.HOYMILES,
        title: 'Hoymiles',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.NEP,
        logo: InverterManufacturerEnum.NEP,
        title: 'NEP',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.PANASONIC,
        logo: InverterManufacturerEnum.PANASONIC,
        title: 'Panasonic',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.FRANKLINWH,
        logo: InverterManufacturerEnum.FRANKLINWH,
        title: 'FranklinWH',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.SONNEN,
        logo: InverterManufacturerEnum.SONNEN,
        title: 'Sonnen',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.TESLA,
        logo: InverterManufacturerEnum.TESLA,
        title: 'Tesla',
        models: TESLA_INVERTER_MODELS,
        deviceTypes: TESLA_DEVICE_TYPES,
        order: 4,
        isSupported: feature('tesla-integration'),
        supportCredentials: true,
        getSiteUrl: id => `https://powerhub.energy.tesla.com/${id ? `site/${id}` : ''}`,
        systemIdGuide: <div>
            <h3 className="flex gap-3 items-center text-xl font-semibold text-gray-900">
                <Logo type={InverterManufacturerEnum.TESLA} className="w-6 shrink-0"/>
                How to Find Tesla Device Identification Number (DIN)
            </h3>

            <div className="mt-6 px-4">
                <div className="p-4 bg-primary-25 text-gray-750 text-sm rounded">
                    <p>Follow these simple steps to seamlessly locate the DIN for your Tesla System:</p>
                    <ul className="mt-4 pl-4 leading-5.5 list-decimal">
                        <li>
                            Log into your <a
                                href="https://auth.tesla.com/"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold underline"
                            >Tesla Powerhub Portal</a>
                        </li>
                        <li>
                            Locate the DIN in the third column of your company site list.
                        </li>
                        <li>
                            If the DIN is not visible, <span className="font-semibold">click into the Site
                            Profile</span>.
                        </li>
                        <li>
                            <span className="font-semibold">
                                Copy the DIN from one of the available components
                            </span> shown in the Devices
                            section: <span className="font-semibold">
                                Powerwall
                            </span>, <span className="font-semibold">
                                Battery
                            </span>, or <span className="font-semibold">Inverter</span>. You only need one DIN.
                        </li>
                    </ul>
                </div>

                <Image className="mt-3" src="/assets/images/gifs/tesla-system-id-guide.gif"/>
            </div>
        </div>
    },
    {
        key: InverterManufacturerEnum.TIGO,
        logo: InverterManufacturerEnum.TIGO,
        title: 'Tigo',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.SMA,
        logo: InverterManufacturerEnum.SMA,
        title: 'SMA',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.FOXESS,
        logo: InverterManufacturerEnum.FOXESS,
        title: 'FoxESS',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.POINTGUARD,
        logo: InverterManufacturerEnum.POINTGUARD,
        title: 'PointGuard',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.QCELLS,
        logo: InverterManufacturerEnum.QCELLS,
        title: 'Qcells',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.FORTRESS,
        logo: InverterManufacturerEnum.FORTRESS,
        title: 'Fortress',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.EG4,
        logo: InverterManufacturerEnum.EG4,
        title: 'EG4',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.LUNAR_ENERGY,
        logo: InverterManufacturerEnum.LUNAR_ENERGY,
        title: 'Lunar Energy',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.ECOFLOW,
        logo: InverterManufacturerEnum.ECOFLOW,
        title: 'EcoFlow',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.CANADIAN_SOLAR,
        logo: InverterManufacturerEnum.CANADIAN_SOLAR,
        title: 'Canadian Solar',
        supportCredentials: true
    },
    {
        key: InverterManufacturerEnum.OTHER,
        title: 'Other',
        supportCredentials: true
    }
]
